import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { QRCodeCanvas } from "qrcode.react";

import * as Yup from "yup";
import valid from "card-validator";
import { useFormik } from "formik";

import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Checkbox,
  createTheme,
  Divider,
  Grid2,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";

import {
  IconArrowLeft,
  IconArrowRight,
  IconBuildingStore,
  IconCopy,
  IconCreditCard,
  IconCurrencyReal,
  IconLock,
  IconWallet,
} from "@tabler/icons-react";

import ContainerLoading from "../components/ContainerLoading";
import { Input } from "../components/Input";

import { checkoutCreditCard, checkoutListPayment } from "../services/checkout";

import {
  formatCpfCnpj,
  formatPrice,
  formatDate,
  formatCvc,
  formatExpiryDate,
  formatCreditCard,
  unMask,
  formatPaymentStatus,
} from "../helpers/format";

import { toast } from "react-toastify";
import BoxLoading from "../components/BoxLoading";
import SelectComponent from "../components/SelectComponent";
import baseTheme from "../theme/theme";
import logo from "../assets/svg/logo.svg";

const validationSchema = Yup.object().shape({
  cardNumber: Yup.string()
    .required("Número do cartão é obrigatório")
    .test(
      "isValidCardNumber",
      "Número do cartão inválido",
      (value) => valid.number(value).isValid // Using card-validator
    ),
  cardholderName: Yup.string().required("Nome no cartão é obrigatório"),
  expiryDate: Yup.string()
    .required("Data de vencimento é obrigatória")
    .matches(/^(0[1-9]|1[0-2])\/?([0-9]{2})$/, "Data de vencimento inválida"),
  cvv: Yup.string()
    .required("CVC é obrigatório")
    .matches(/^[0-9]{3,4}$/, "CVC inválido"),
  cardDocument: Yup.string()
    .required("CPF/CNPJ é obrigatório")
    .matches(/^[0-9]{11,14}$/, "CPF/CNPJ inválido"),
});

const checkoutTheme = createTheme({
  ...baseTheme,
  components: {
    ...baseTheme.components,

    MuiInputBase: {
      ...baseTheme.components?.MuiInputBase,
      styleOverrides: {
        ...baseTheme.components?.MuiInputBase?.styleOverrides,
        root: (params: any) => {
          const originalRoot =
            typeof baseTheme.components?.MuiInputBase?.styleOverrides?.root ===
            "function"
              ? baseTheme.components.MuiInputBase.styleOverrides.root(params)
              : baseTheme.components?.MuiInputBase?.styleOverrides?.root;

          const safeOriginalRoot =
            originalRoot && typeof originalRoot === "object"
              ? originalRoot
              : {};

          return {
            ...safeOriginalRoot,
            backgroundColor: "#FFF",
            color: "#000",
          };
        },
      },
    },

    MuiInputLabel: {
      ...baseTheme.components?.MuiInputLabel,
      styleOverrides: {
        ...baseTheme.components?.MuiInputLabel?.styleOverrides,
        root: (params: any) => {
          const originalRoot =
            typeof baseTheme.components?.MuiInputLabel?.styleOverrides?.root ===
            "function"
              ? baseTheme.components.MuiInputLabel.styleOverrides.root(params)
              : baseTheme.components?.MuiInputLabel?.styleOverrides?.root;

          const safeOriginalRoot =
            originalRoot && typeof originalRoot === "object"
              ? originalRoot
              : {};

          return {
            ...safeOriginalRoot,
            color: "#000",
            "&.Mui-focused": {
              color: "#000",
            },
            "&.MuiInputLabel-shrink": {
              color: "#000",
            },
          };
        },
      },
    },
  },
});

const Checkout = () => {
  const param: any = useParams();

  const [saveCreditCard, setSaveCreditCard] = useState(false);
  const [enableCreditCard, setEnableCreditCard] = useState(false);
  const [installments, setStInstallments] = useState("1");
  const [fullScreenPayment, setFullScreenPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState("");

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["checkoutListPayment", param?.webToken],
    queryFn: async () => await checkoutListPayment(param?.webToken),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: checkoutCreditCard,
    onSuccess: () => {
      toast.success("Pagamento processado com sucesso.");
      refetch();
      setFullScreenPayment(false);
    },
    onError: () => {
      toast.error(
        "Não conseguimos processar seu pagamento, entre em contato com seu cartão de crédito"
      );
    },
  });

  const store = data?.store;

  const {
    values,
    setFieldValue,
    handleChange,
    errors,
    touched,
    handleBlur,
    isValid,
    handleSubmit,
    dirty,
  } = useFormik({
    initialValues: {
      cardNumber: "",
      cardholderName: "",
      cardDocument: "",
      expiryDate: "",
      cvv: "",
    },
    validationSchema,
    onSubmit: (values: any) => {
      mutate({
        ...values,
        webToken: param?.webToken,
        saveCreditCard,
        installments,
      });
    },
  });

  const getColor = () => {
    if (
      [
        "CONFIRMED",
        "CONFIRMED_INSTALLMENT",
        "PAID",
        "RECEIVED_IN_CASH",
      ].includes(data?.status)
    ) {
      return "#2E8B57";
    }

    if (["OVERDUE"].includes(data?.status)) {
      return "#B22222";
    }

    return "#ffc400";
  };

  useEffect(() => {
    if (data) {
      if (data?.studentOnPlan) {
        const isCreditCard = data?.studentOnPlan?.plan
          ? data?.studentOnPlan?.plan?.paymentsType
              ?.split(",")
              .includes("CREDIT_CARD")
          : false;

        setEnableCreditCard(isCreditCard);
      }

      if (data?.order?.paymentType) {
        const isCreditCard = data?.order?.paymentType
          ? data?.order?.paymentType?.split(",").includes("CREDIT_CARD")
          : false;

        setEnableCreditCard(isCreditCard);
      }

      if (data?.creditCardInstallments && data?.billingType === "CREDIT_CARD") {
        setEnableCreditCard(true);
      }
    }
  }, [data]);

  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#F7F7F7";

    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);

  const paymentMethodBoxStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#EFEFEF",
    borderRadius: "14px",
    padding: "12px 16px",
    cursor: "pointer",
    width: "100%",
    maxWidth: "500px",
    margin: "0 auto",
    "&:hover": {
      backgroundColor: "#ebebeb",
    },
  };

  return (
    <ThemeProvider theme={checkoutTheme}>
      <ContainerLoading maxWidth={"xs"} sx={{ width: "100%", padding: "0" }}>
        <BoxLoading
          sx={{
            minHeight: "100vh",
            position: "relative",
            backgroundColor: "#fff",
            minWidth: "100%",
          }}
          light
          loading={isLoading || isFetching || isPending}
        >
          {error ? (
            <Stack
              justifyContent={"center"}
              alignItems={"center"}
              height={"100vh"}
              width={"100%"}
              textAlign={"center"}
            >
              <Typography variant="h1" color="primary" fontWeight={700}>
                Ops!
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} mt={1}>
                Aconteceu um erro.
              </Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                Cobrança não encontrada.
              </Typography>
              <Button
                variant="contained"
                sx={{ mt: 4 }}
                size="large"
                onClick={() => {
                  window.open(
                    "https://wa.me/551926260132?text=Preciso%20de%20suporte"
                  );
                }}
              >
                Entrar em contato com suporte
              </Button>
            </Stack>
          ) : (
            <Grid2
              container
              spacing={1}
              mt={2}
              sx={{
                backgroundColor: "#fefdfd",
                maxWidth: "550px",
                width: "100%",
                padding: "0.6rem",
                paddingTop: "1rem",
              }}
              borderRadius={2}
              flexDirection={"column"}
              justifyContent={"center"}
              alignContent={"center"}
              margin={"auto"}
            >
              {fullScreenPayment ? (
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ pb: "0.7rem" }}
                    width={"100%"}
                  >
                    <Stack justifyContent={"center"} alignContent={"center"}>
                      <Button
                        onClick={() => setFullScreenPayment(false)}
                        sx={{ width: "32px" }}
                      >
                        <IconArrowLeft color="black" />
                      </Button>
                    </Stack>

                    <Stack>
                      <img
                        src={logo}
                        style={{
                          width: "32px",
                          borderRadius: "10px",
                          height: "32px",
                        }}
                      />
                    </Stack>

                    <Stack sx={{ width: "32px" }} />
                  </Stack>
                  <Stack>
                    <Divider color="#EFEFEF" />

                    <Stack
                      alignItems="center"
                      justifyContent="space-between"
                      flexDirection={"row"}
                      my={1}
                    >
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        flexDirection={"row"}
                      >
                        <Stack
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            backgroundColor: "#EFEFEF",
                            padding: "0.4rem",
                            borderRadius: "100%",
                          }}
                        >
                          <IconBuildingStore color="black" size={14} />
                        </Stack>
                        <Stack>
                          <Typography
                            color="black"
                            fontWeight={700}
                            fontSize="14px"
                            noWrap
                            sx={{ textTransform: "uppercase" }}
                          >
                            {store?.name}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack direction="row" justifyContent="center">
                        <Typography
                          color="black"
                          sx={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#7600ED",
                          }}
                        >
                          {formatPrice(data?.value)}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Divider color="#EFEFEF" />
                  </Stack>
                </>
              ) : (
                <Box width={"100%"}>
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    flexDirection={"row"}
                    sx={{ pb: "1rem" }}
                  >
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        backgroundColor: "#EFEFEF",
                        padding: "0.6rem",
                        borderRadius: "100%",
                      }}
                    >
                      <IconBuildingStore color="black" size={18} />
                    </Stack>
                    <Stack>
                      <Typography
                        color="black"
                        fontWeight={700}
                        fontSize="14px"
                        noWrap
                        sx={{ textTransform: "uppercase" }}
                      >
                        {store?.name}
                      </Typography>
                      <Typography
                        color="black"
                        fontWeight={500}
                        fontSize="14px"
                        noWrap
                      >
                        {formatCpfCnpj(store?.document)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Divider color="#EFEFEF" />

                  <Grid2
                    size={12}
                    sx={{ backgroundColor: getColor() }}
                    padding={1}
                    borderRadius={2}
                  >
                    {![
                      "CONFIRMED",
                      "CONFIRMED_INSTALLMENT",
                      "PAID",
                      "RECEIVED_IN_CASH",
                    ].includes(data?.status) && (
                      <Typography fontWeight={700} fontSize={12}>
                        Fatura{" "}
                        {formatPaymentStatus(
                          data?.status === "CONFIRMED" ? "PAID" : data?.status
                        )}
                      </Typography>
                    )}

                    {[
                      "CONFIRMED",
                      "CONFIRMED_INSTALLMENT",
                      "PAID",
                      "RECEIVED_IN_CASH",
                    ].includes(data?.status) && (
                      <Typography fontWeight={700} fontSize={12}>
                        Fatura paga em {formatDate(data?.paymentDate)}
                      </Typography>
                    )}
                  </Grid2>
                </Box>
              )}

              {!fullScreenPayment && (
                <Stack
                  flexDirection={"column"}
                  justifyContent={"space-between"}
                  gap={1}
                  mt={6}
                  mb={6}
                >
                  <Stack alignItems={"center"} justifyContent="center">
                    <Typography
                      color="black"
                      sx={{ fontSize: "14px", fontWeight: 500 }}
                    >
                      Valor da cobrança
                    </Typography>
                    <Typography
                      color="black"
                      sx={{ fontSize: "34px", fontWeight: 700, mt: "1px" }}
                    >
                      {formatPrice(data?.value)}
                    </Typography>
                  </Stack>
                </Stack>
              )}

              {![
                "CONFIRMED",
                "PAID",
                "CONFIRMED_INSTALLMENT",
                "RECEIVED_IN_CASH",
              ].includes(data?.status) && (
                <>
                  {!fullScreenPayment ? (
                    <Stack spacing={1} sx={{ width: "100%" }}>
                      {enableCreditCard && (
                        <Box
                          onClick={() => {
                            setSelectedPayment("CREDIT_CARD");
                            setFullScreenPayment(true);
                          }}
                          sx={paymentMethodBoxStyle}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <IconCreditCard
                              color="#7600ED"
                              style={{ width: "20px", height: "20px" }}
                            />
                            <Box>
                              <Typography
                                fontWeight={600}
                                fontSize={"16px"}
                                color="black"
                              >
                                Cartão de crédito
                              </Typography>
                            </Box>
                          </Stack>
                          <IconArrowRight color="#7600ED" />
                        </Box>
                      )}

                      <Box
                        onClick={() => {
                          setSelectedPayment("PIX");
                          setFullScreenPayment(true);
                        }}
                        sx={paymentMethodBoxStyle}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          {/* <Box sx={iconCircleStyle}>
                            <img
                              src={Piximg}
                              alt="Pix"
                              style={{ width: "20px", height: "20px" }}
                            />
                          </Box> */}

                          <IconCurrencyReal
                            color="#7600ED"
                            style={{ width: "20px", height: "20px" }}
                          />
                          <Box>
                            <Typography
                              fontWeight={600}
                              fontSize={"16px"}
                              color="black"
                            >
                              Pagar com Pix
                            </Typography>
                          </Box>
                        </Stack>
                        <IconArrowRight color="#7600ED" />
                      </Box>
                    </Stack>
                  ) : (
                    <Stack
                      sx={{
                        width: "100%",
                        minHeight: "50vh",
                        backgroundColor: "#fff",
                      }}
                    >
                      {selectedPayment === "PIX" && (
                        <Stack
                          direction="column"
                          sx={{
                            padding: "5px",
                            mt: 2,
                            mb: 2,
                          }}
                        >
                          <Typography
                            fontSize={"18px"}
                            color="black"
                            fontWeight={700}
                          >
                            {selectedPayment === "PIX" ? "Pagar com Pix" : ""}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.5,
                              fontSize: "14px",
                            }}
                            color="#575757"
                          >
                            Escaneie o QR code ou copie o código
                          </Typography>
                        </Stack>
                      )}

                      {selectedPayment === "PIX" && (
                        <Stack direction="column" gap={"32px"}>
                          <Stack
                            justifyContent="center"
                            alignItems="center"
                            spacing={2}
                            sx={{
                              flexGrow: 1,
                              padding: "24px",
                              border: "1px solid rgba(17, 17, 17, 0.1)",
                              borderRadius: "16px",
                              paddingY: "34px",
                            }}
                          >
                            <QRCodeCanvas
                              value={data?.bankPixCode}
                              style={{ width: "170px", height: "170px" }}
                            />
                          </Stack>

                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={<IconCopy />}
                            onClick={async () => {
                              await navigator.clipboard.writeText(
                                data?.bankPixCode
                              );
                              toast.info("Código PIX copiado!");
                            }}
                            sx={{
                              backgroundColor: "#7600ED",
                              width: "100%",
                              mb: "1rem",
                              paddingLeft: "1.25rem",
                              paddingRight: "1.25rem",
                              height: "2.2rem",
                              fontSize: "14px",
                            }}
                          >
                            Copiar código do QR Code
                          </Button>
                          <Divider color="#EFEFEF" />
                          <Stack sx={{ color: "black" }} spacing={1}>
                            <Typography fontWeight={700} pb={1}>
                              Instruções de Pagamento
                            </Typography>
                            <Typography
                              fontSize={12}
                              color="rgba(17, 17, 17, 0.7)"
                            >
                              1. Abra o aplicativo do seu banco;
                            </Typography>
                            <Typography
                              fontSize={12}
                              color="rgba(17, 17, 17, 0.7)"
                            >
                              2. Entre na área Pix e busque a opção de pagar com
                              Pix Copia e Cola;
                            </Typography>
                            <Typography
                              fontSize={12}
                              color="rgba(17, 17, 17, 0.7)"
                            >
                              3. Insira o código copiado desta cobrança;
                            </Typography>
                            <Typography
                              fontSize={12}
                              color="rgba(17, 17, 17, 0.7)"
                            >
                              4. Revise o pagamento e confirme.
                            </Typography>

                            <Typography
                              fontSize={12}
                              color="rgba(17, 17, 17, 0.7)"
                              pt={2}
                            >
                              {" "}
                              Se estiver no computador, abra o aplicativo do seu
                              banco e escaneie a imagem acima com a câmera do
                              celular.{" "}
                            </Typography>
                          </Stack>
                        </Stack>
                      )}

                      {selectedPayment === "CREDIT_CARD" &&
                        enableCreditCard && (
                          <Stack>
                            <Stack
                              direction="column"
                              sx={{
                                padding: "5px",
                                mb: 2,
                                mt: 2,
                              }}
                            >
                              <Typography
                                fontSize={"18px"}
                                color="black"
                                fontWeight={700}
                              >
                                Pagar com Cartão
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.5,
                                  fontSize: "14px",
                                }}
                                color="#575757"
                              >
                                Preencha os dados abaixo para realizar o
                                pagamento
                              </Typography>
                            </Stack>
                            <Box sx={{ padding: "5px" }}>
                              <Grid2 container spacing={2}>
                                {Boolean(
                                  data?.order?.installments ||
                                    data?.creditCardInstallments
                                ) && (
                                  <Grid2 size={12}>
                                    <SelectComponent
                                      name="installments"
                                      placeholder="Parcelas"
                                      value={installments}
                                      onChange={(e: any) =>
                                        setStInstallments(e.value)
                                      }
                                      options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
                                        .filter(
                                          (i) =>
                                            Number(i) <=
                                            Number(
                                              data?.order?.installments ||
                                                data?.creditCardInstallments
                                            )
                                        )
                                        .filter(
                                          (i) => data?.value / Number(i) >= 10
                                        )
                                        .map((i) => ({
                                          value: String(i),
                                          label: `Até ${i}x de ${formatPrice(
                                            data?.value / i
                                          )}`,
                                        }))}
                                    />
                                  </Grid2>
                                )}

                                <Grid2 size={{ xs: 12 }}>
                                  <Input
                                    label="Nome igual no cartão"
                                    id="cardholderName"
                                    name="cardholderName"
                                    type="text"
                                    sx={{
                                      "& label": {
                                        color: "#7600ED",
                                      },
                                    }}
                                    fullWidth
                                    value={values.cardholderName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      touched.cardholderName &&
                                      !!errors.cardholderName
                                    }
                                  />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                  <Input
                                    label="CPF/CNPJ"
                                    id="cardDocument"
                                    name="cardDocument"
                                    type="text"
                                    fullWidth
                                    sx={{
                                      "& label": {
                                        color: "#7600ED",
                                      },
                                    }}
                                    value={formatCpfCnpj(values.cardDocument)}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "cardDocument",
                                        unMask(e.target.value)
                                      )
                                    }
                                    onBlur={handleBlur}
                                    autoComplete="off"
                                    error={
                                      touched.cardDocument &&
                                      !!errors.cardDocument
                                    }
                                  />
                                </Grid2>
                                <Grid2 size={12}>
                                  <Input
                                    label="Número do cartão"
                                    id="cardNumber"
                                    name="cardNumber"
                                    fullWidth
                                    sx={{
                                      "& label": {
                                        color: "#7600ED",
                                      },
                                    }}
                                    value={formatCreditCard(values.cardNumber)}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "cardNumber",
                                        formatCreditCard(e.target.value)
                                      )
                                    }
                                    onBlur={handleBlur}
                                    error={
                                      touched.cardNumber && !!errors.cardNumber
                                    }
                                  />
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                  <Input
                                    label="Vencimento"
                                    id="expiryDate"
                                    name="expiryDate"
                                    type="text"
                                    sx={{
                                      "& label": {
                                        color: "#7600ED",
                                      },
                                    }}
                                    fullWidth
                                    value={formatExpiryDate(values.expiryDate)}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "expiryDate",
                                        formatExpiryDate(e.target.value)
                                      )
                                    }
                                    onBlur={handleBlur}
                                    error={
                                      touched.expiryDate && !!errors.expiryDate
                                    }
                                  />
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                  <Input
                                    label="CVC"
                                    id="cvv"
                                    name="cvv"
                                    type="text"
                                    sx={{
                                      "& label": {
                                        color: "#7600ED",
                                      },
                                    }}
                                    fullWidth
                                    value={formatCvc(values.cvv)}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "cvv",
                                        formatCvc(e.target.value)
                                      )
                                    }
                                    onBlur={handleBlur}
                                    error={touched.cvv && !!errors.cvv}
                                  />
                                </Grid2>

                                {data?.studentOnPlan && (
                                  <Grid2 size={12}>
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                      sx={{
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Checkbox
                                        sx={{ color: "#7600ED" }}
                                        checked={saveCreditCard}
                                        onClick={() =>
                                          setSaveCreditCard(!saveCreditCard)
                                        }
                                      />
                                      <Typography color="black">
                                        Usar cartão para cobranças futuras
                                      </Typography>
                                    </Stack>
                                  </Grid2>
                                )}

                                <Grid2 size={12}>
                                  <Button
                                    variant="contained"
                                    fullWidth
                                    sx={{ mt: 2 }}
                                    startIcon={<IconCreditCard />}
                                    onClick={() => handleSubmit()}
                                    disabled={!isValid || !dirty}
                                  >
                                    Pagar
                                  </Button>
                                </Grid2>
                              </Grid2>
                            </Box>
                          </Stack>
                        )}
                    </Stack>
                  )}
                </>
              )}

              {!fullScreenPayment && (
                <>
                  <Divider color="#EFEFEF" sx={{ mt: 2 }} />
                  <Stack mt={2}>
                    <Stack
                      mb={"12px"}
                      direction="row"
                      alignItems={"center"}
                      gap={"6px"}
                    >
                      <IconWallet size={20} color="black" />
                      <Typography
                        fontWeight={700}
                        fontSize={"14px"}
                        color="black"
                      >
                        Detalhes da cobrança
                      </Typography>
                    </Stack>
                    <Stack
                      mb={"10px"}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography
                        fontWeight={700}
                        fontSize={"14px"}
                        color="black"
                      >
                        Nome
                      </Typography>
                      <Typography color="black" fontSize={"14px"}>
                        {data?.payer?.fullName}
                      </Typography>
                    </Stack>
                    <Stack
                      mb={"10px"}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography
                        fontWeight={700}
                        fontSize={"14px"}
                        color="black"
                      >
                        Vencimento
                      </Typography>
                      <Typography fontSize={"14px"} color="black">
                        {formatDate(data?.dueDate)}
                      </Typography>
                    </Stack>

                    <Stack direction="column" justifyContent="space-between">
                      <Typography
                        fontWeight={700}
                        fontSize={"14px"}
                        color="black"
                      >
                        Descrição
                      </Typography>
                      <Typography color="black" fontSize={"14px"} mt={"4px"}>
                        {data?.description}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Divider color="#EFEFEF" sx={{ mt: 2 }} />
                </>
              )}
            </Grid2>
          )}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              backgroundColor: "#F0F1F5",
              zIndex: 1300, // Garante que fique acima de outros elementos
              padding: "1rem",
            }}
          >
            <IconLock size={16} color="#333" />
            <Typography
              fontWeight={700}
              ml={0.5}
              fontSize={"14px"}
              color="black"
            >
              Desenvolvido por Fight Go
            </Typography>
          </Stack>
        </BoxLoading>
      </ContainerLoading>
    </ThemeProvider>
  );
};

export default Checkout;
